<template>
  <div class="wrapper">
    <h3 class="title">{{ parseTranslation(BASICS_SETTINGS) }}</h3>

    <div class="basic-settings">
      <div>
        <div class="field">
          <label class="label">{{ parseTranslation(GENDER) }}:</label>

          <input type="radio" id="male" value="male" v-model="form.gender" />
          <label for="male">{{ parseTranslation(MALE) }}</label>

          <input type="radio" id="female" value="female" v-model="form.gender" />
          <label for="female">{{ parseTranslation(FEMALE) }}</label>

          <input type="radio" id="both" value="both" v-model="form.gender" />
          <label for="both">{{ parseTranslation(BOTH) }}</label>
        </div>

        <div class="field age">
          <div class="max-min">
            <div v-for="(age, index) in form.ages" :key="index">
              <label class="label"
                >{{ parseTranslation(AGE_RANGE) }} {{ index + 1 }}:</label
              >

              <div>
                <label for="minimum">{{ parseTranslation(MINIMUM) }}:</label>
                <input
                  :class="{ 'red-text': Number(age.min) < MIN_ACCEPTED_AGE }"
                  v-model.number="age.min"
                  @change="minAgeValidation(index)"
                  type="number"
                  id="minimum"
                  :min="getMinAge(index)"
                  :max="age.max"
                  :title="parseTranslation(PLACEHOLDER_MINIMUM_AGE_RANGE)"
                />

                <label for="maximum">{{ parseTranslation(MAXIMUM) }}:</label>
                <input
                  :class="{ 'red-text': Number(age.max) < MIN_ACCEPTED_AGE }"
                  v-model.number="age.max"
                  type="number"
                  @change="maxAgeValidation(index)"
                  id="maximum"
                  :min="age.min"
                  :max="getMaxAge(index)"
                  :title="parseTranslation(PLACEHOLDER_MAXIMUM_AGE_RANGE)"
                />
              </div>

              <button
                @click="form.ages.splice(index, 1)"
                class="trash"
                v-if="index !== 0"
              >
                <Delete variant="red" />
              </button>
            </div>
          </div>

          <button
            @click="addAgeRange"
            class="add-btn"
            :disabled="lastMaxAge === MAX_ACCEPTED_AGE"
          >
            +
          </button>
        </div>
      </div>

      <div v-if="!loaderFilters">
        <div class="field">
          <label class="geo">{{ parseTranslation(GEO) }}:</label>

          <input type="radio" id="region" value="ows-18" v-model="form.geo" />
          <label for="region">{{ parseTranslation(REGION) }}</label>

          <input
            v-if="this.filters.length > 5 && this.filters[6].answers.length > 0"
            type="radio"
            id="region2"
            value="ows-25"
            v-model="form.geo"
          />
          <label
            v-if="this.filters.length > 5 && this.filters[6].answers.length > 0"
            for="region2"
            >{{ parseTranslation(REGION) }} 2</label
          >

          <input type="radio" id="state" value="ows-16" v-model="form.geo" />
          <label for="state">{{ parseTranslation(STATE) }}</label>

          <input type="radio" id="city" value="ows-17" v-model="form.geo" />
          <label for="city">{{ parseTranslation(CITY) }}</label>

          <input type="radio" id="metropolitan" value="ows-19" v-model="form.geo" />
          <label for="metropolitan">{{ parseTranslation(METROPOLITAN) }}</label>
        </div>

        <div v-if="form.geo !== ''">
          <filter-box
            :setGeoValuesCallback="updateGeoValues"
            :selected="geoValuesInitial"
            :filter="geoFilter"
            :states="states"
            :id="geoFilter.qid"
            :loaderCities="loaderCities"
            :getStateCallback="getFilters"
          />
        </div>
      </div>

      <div v-else>
        <Loader />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import Loader from '@/components/loading/Loader-2'

import FilterBox from './FilterBox.vue'

import Delete from '@/components/imgs/Delete'

import * as translationBasicsSettings from './BasicSettings.translations'

import { UNITARY_SHIFT } from '@/utils/constants'

import { getLastIndex } from '@/utils/array'

const { default: translations, ...translationKeys } = translationBasicsSettings

const owQuestions = {
  age: 'ows-1',
  gender: 'ows-2',
  state: 'ows-16',
  city: 'ows-17',
  region: 'ows-18',
  metropolitan: 'ows-19',
  region2: 'ows-25',
}

const MIN_ACCEPTED_AGE = 13
const MAX_ACCEPTED_AGE = 99
const ZERO = 0

export default {
  components: { FilterBox, Loader, Delete },

  props: {
    country: {
      required: true,
    },

    survey: {
      required: true,
    },
  },

  data() {
    return {
      translations,
      ...translationKeys,
      filters: null,
      loaderFilters: false,
      regionAlt: false,
      form: {
        gender: 'both',
        ages: [{ min: MIN_ACCEPTED_AGE, max: MAX_ACCEPTED_AGE }],
        geo: '',
        geoValues: [],
      },
      geoValuesInitial: null,
      loaderCities: false,
      owQuestions,
      owQuestionHandlers: {},
      MIN_ACCEPTED_AGE,
      MAX_ACCEPTED_AGE,
    }
  },

  computed: {
    geoFilter() {
      if (!this.filters) return

      return this.filters.find(qst => qst.code === this.form.geo)
    },

    states() {
      if (!this.filters) return

      const state = this.filters.find(qst => qst.code === owQuestions.state)

      return state?.answers
    },

    questions() {
      let array = []

      if (this.filters) {
        const genderAnswers = this.filters
          .find(filter => filter.code === owQuestions.gender)
          .answers.filter(
            answer =>
              answer.label.toLowerCase() === this.form.gender ||
              this.form.gender === 'both',
          )
        const ageAnswers = this.form.ages
          .filter(
            age =>
              age.max !== '' && age.max !== 0 && +age.max >= +age.min && age.min !== '',
          )
          .map((age, index) => ({
            code: `${index + 1}`,
            label: `${age.min}-${age.max}`,
          }))

        array = this.filters
          .filter(
            filter =>
              filter.code === owQuestions.gender ||
              (filter.code === owQuestions.age && ageAnswers.length) ||
              (filter.code === this.form.geo && this.form.geoValues.length),
          )
          .map(filter => {
            if (filter.code === owQuestions.gender) {
              return { ...filter, answers: genderAnswers }
            } else if (filter.code === owQuestions.age) {
              return { ...filter, answers: ageAnswers }
            } else if (filter.code === this.form.geo) {
              return { ...filter, answers: this.form.geoValues }
            } else return filter
          })
      }

      return array
    },

    lastMaxAge() {
      const lastAgesIndex = getLastIndex(this.form.ages)

      const lastMaxAge = Number(this.form.ages[lastAgesIndex].max)

      return lastMaxAge
    },
  },

  watch: {
    questions() {
      this.setBasicQuestions(this.questions)
    },
  },

  methods: {
    ...mapActions(['sendMessage', 'setBasicQuestions', 'executeSurveyAPICall']),

    addAgeRange() {
      if (this.lastMaxAge === this.MAX_ACCEPTED_AGE) return

      const newAge = this.lastMaxAge + UNITARY_SHIFT

      this.form.ages.push({
        min: newAge,
        max: newAge,
      })
    },

    getMaxAge(index) {
      let maxAge = this.MAX_ACCEPTED_AGE

      if (index !== getLastIndex(this.form.ages)) {
        const nextIndex = index + UNITARY_SHIFT

        maxAge = Number(this.form.ages[nextIndex].min) - UNITARY_SHIFT
      }

      return maxAge
    },

    getMinAge(index) {
      let minAge = this.MIN_ACCEPTED_AGE

      if (index !== ZERO) {
        const prevIndex = index - UNITARY_SHIFT

        minAge = Number(this.form.ages[prevIndex].max) + UNITARY_SHIFT
      }

      return minAge
    },

    minAgeValidation(index) {
      const maxAge = this.form.ages[index].max

      const minAge = this.form.ages[index].min

      if (minAge < this.MIN_ACCEPTED_AGE) {
        this.form.ages[index].min = this.MIN_ACCEPTED_AGE
      }

      if (minAge > maxAge) {
        this.form.ages[index].min = maxAge
      }

      if (index !== ZERO) {
        const prevIndex = index - UNITARY_SHIFT

        const prevMaxAge = Number(this.form.ages[prevIndex].max)

        if (minAge <= prevMaxAge) {
          this.form.ages[index].min = prevMaxAge + UNITARY_SHIFT
        }
      }
    },

    maxAgeValidation(index) {
      const maxAge = this.form.ages[index].max

      const minAge = this.form.ages[index].min

      if (maxAge > this.MAX_ACCEPTED_AGE) {
        this.form.ages[index].max = this.MAX_ACCEPTED_AGE
      }

      if (maxAge < minAge) {
        this.form.ages[index].max = minAge
      }

      if (index !== getLastIndex(this.form.ages)) {
        const nextIndex = index + UNITARY_SHIFT

        const nextMinAge = Number(this.form.ages[nextIndex].min)

        if (maxAge > nextMinAge) {
          this.form.ages[index].max = nextMinAge - UNITARY_SHIFT
        }
      }
    },

    updateGeoValues(geoValues) {
      this.form.geoValues = geoValues
    },

    async getFilters(state) {
      if (!state) this.loaderFilters = true
      else this.loaderCities = true

      const payload = {
        country: this.country.value,
        state: state || undefined,
      }

      const filterData = await this.executeSurveyAPICall({
        method: 'POST',
        path: 'sample/filters',
        payload,
      })

      if (!filterData) return

      this.filters = filterData.questions

      this.loaderFilters = false
      this.loaderCities = false
    },

    handleGenderQuestion(genderQuestion) {
      const { answers } = genderQuestion

      if (!answers?.length) return

      const MALE_CODE = '1'
      // const FEMALE_CODE = '2'

      if (answers.length === 2) return (this.form.gender = 'both')

      const malePresent = answers.find(({ code }) => `${code}` === MALE_CODE)

      const gender = malePresent ? 'male' : 'female'

      this.form.gender = gender
    },

    handleAgeQuestion(ageQuestion) {
      const { answers } = ageQuestion

      this.form.ages = answers.map(answer => {
        const [min, max] = answer.label.split('-')

        return {
          min,
          max,
        }
      })
    },

    handleRegionQuestion(regionQuestion, owQuestion) {
      const { answers } = regionQuestion

      this.form.geo = owQuestion

      this.geoValuesInitial = answers
    },

    handleRegionQuestionTwo(regionQuestion, owQuestion) {
      const { answers } = regionQuestion

      this.form.geo = owQuestion

      this.geoValuesInitial = answers
    },

    handleStateQuestion(stateQuestion, owQuestion) {
      const { answers } = stateQuestion

      this.form.geo = owQuestion

      this.geoValuesInitial = answers
    },

    handleCityQuestion(cityQuestion, owQuestion) {
      const { answers } = cityQuestion

      this.form.geo = owQuestion

      this.geoValuesInitial = answers
    },

    handleMetropolitanQuestion(metropolitanQuestion, owQuestion) {
      const { answers } = metropolitanQuestion

      this.form.geo = owQuestion

      this.geoValuesInitial = answers
    },

    getQuestionsMapping(questions) {
      const questionMapping = questions.reduce(
        (acc, nextQuestion) => ({
          ...acc,
          [nextQuestion.code]: nextQuestion,
        }),
        {},
      )

      return questionMapping
    },

    handleInitialState() {
      const sampleGroup = this.survey.questions_groups.find(
        group => group.group_code === 'Sample',
      )

      if (!sampleGroup) return

      const { questions } = sampleGroup

      const questionMapping = this.getQuestionsMapping(questions)

      Object.entries(this.owQuestionHandlers).forEach(([owQuestion, handler]) => {
        const question = questionMapping[owQuestions[owQuestion]]

        if (question) handler(question, owQuestions[owQuestion])
      })
    },
  },

  created() {
    this.getFilters()

    this.owQuestionHandlers = {
      age: this.handleAgeQuestion.bind(this),
      gender: this.handleGenderQuestion.bind(this),
      state: this.handleStateQuestion.bind(this),
      city: this.handleCityQuestion.bind(this),
      region: this.handleRegionQuestion.bind(this),
      metropolitan: this.handleMetropolitanQuestion.bind(this),
      region2: this.handleRegionQuestionTwo.bind(this),
    }

    this.handleInitialState()
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;

  .title {
    display: flex;
    align-items: center;
    @include tipography(16);
    color: $white;
    font-weight: bold;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    white-space: nowrap;
    margin-bottom: 20px;
    margin-top: 20px;
    &::after {
      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background: $white;
      margin-left: 15px;
    }
  }

  .basic-settings {
    display: flex;
    flex-wrap: wrap;

    > div {
      width: 50%;
      @include d(s) {
        width: 100%;
      }
    }

    label {
      @include tipography(14);
      color: $white;
    }

    input[type='number'] {
      border: 1px solid $white;
      border-radius: 3px;
      background: $main-3;
      padding: 5px 10px;
      color: $white;
      @include focus;
    }

    input[type='radio'] {
      margin-left: 20px;
      margin-right: 5px;
      &:nth-of-type(1) {
        margin-left: 0;
      }
    }

    .label {
      margin-right: 20px;
      display: inline-block;
      @include d(s) {
        width: unset;
      }
    }

    .field {
      margin-bottom: 20px;

      &.age {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        .max-min {
          display: flex;
          flex-direction: column;

          > div {
            flex-wrap: wrap;
            display: flex;
            align-items: center;
            margin-bottom: 10px;

            label {
              margin-right: 20px;
            }

            input {
              max-width: 60px;
              margin-right: 10px;

              &.red-text {
                color: $red;
              }
            }
          }
        }
      }
      .geo {
        margin-right: 20px;
      }
    }
  }

  .add-btn {
    margin: 0 0 10px 10px;
    @include tipography(25);
    @include flexMiddle;
    background: $blue;
    border: none;
    border-radius: 50%;
    @include boxSizing(22);
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: $blue-2;
    }

    &:focus {
      outline: none;
    }

    &:disabled {
      @include disabled-button;
    }
  }

  .trash {
    background: none;
    border: none;
    cursor: pointer;
    transition: all 0.3 ease;

    svg {
      height: 19px;
    }

    &:hover {
      transform: scale(1.05);
    }
    &:focus {
      outline: none;
    }
  }

  .light & {
    .title {
      color: $main;

      &::after {
        background: $gray-5;
      }
    }

    .basic-settings {
      label {
        color: $gray-2;
      }

      input[type='number'] {
        border: 1px solid $gray-5;
        background: $white;
        color: $main;
      }
    }
  }
}
</style>
